import { Timestamp } from 'firebase/firestore';

/**
 * Enum representing the status of each letter in a guess
 */
export enum LetterStatus {
  CORRECT = 'correct', // Green - correct letter in correct position
  PRESENT = 'present', // Yellow - correct letter in wrong position
  ABSENT = 'absent', // Gray - letter not in word
  UNUSED = 'unused', // Letter not yet guessed
}

/**
 * Enum representing the status of a Paydle attempt
 */
export enum PaydleStatus {
  IN_PROGRESS = 'in_progress', // Player is still guessing
  WON = 'won', // Player guessed correctly
  LOST = 'lost', // Player ran out of attempts
  PAYMENT_PENDING = 'payment_pending', // Player lost, payment required
  PAYMENT_COMPLETED = 'payment_completed', // Player paid after losing
}

/**
 * Interface representing a single guess in a Paydle attempt
 */
export interface PaydleGuess {
  /** The guessed word (5 letters, uppercase) */
  word: string;
  /** Status for each letter in the guess */
  letterStatuses: LetterStatus[];
  /** When this guess was made */
  timestamp: Date | Timestamp;
}

/**
 * Interface representing a Paydle attempt (one user's play session)
 */
export interface PaydleAttempt {
  /** Unique identifier for the attempt */
  id: string;
  /** ID of the Paydle being played */
  paydleId: string;
  /** Share token used to access the Paydle */
  shareId: string;
  /** Array of guesses made */
  guesses: PaydleGuess[];
  /** Current status of the attempt */
  status: PaydleStatus;
  /** When the attempt was started */
  startedAt: Date | Timestamp;
  /** When the attempt was completed (if finished) */
  completedAt?: Date | Timestamp;
  /** Optional user ID if authenticated */
  userId?: string | null;
  /** Optional user display name if authenticated */
  userName?: string | null;
  /** IP address for anonymous tracking */
  ipAddress?: string | null;
  /** Optional payment proof/screenshot URL */
  paymentProof?: string;
  /** Optional note from player */
  paymentNote?: string;
}

/**
 * Interface representing a Paydle (the word puzzle created by the creator)
 */
export interface Paydle {
  /** Unique identifier for the Paydle */
  id: string;
  /** User ID of the creator */
  creatorId: string;
  /** Creator's display name */
  creatorName?: string;
  /** The 5-letter word to guess (stored in uppercase) */
  word: string;
  /** Amount to pay if player loses */
  price: number;
  /** Optional description/hint */
  description?: string;
  /** Unique token for sharing (public) */
  shareId: string;
  /** Token for creator to manage/view (private) */
  accessToken: string;
  /** Maximum number of guesses allowed (always 6 for Wordle-style) */
  maxAttempts: number;
  /** Whether this Paydle is still accepting plays */
  isActive: boolean;
  /** Creation timestamp */
  createdAt: Date | Timestamp;
  /** Last update timestamp */
  updatedAt: Date | Timestamp;
  /** How many times this has been played */
  totalAttempts: number;
  /** How many players have won */
  totalWins: number;
  /** How many players have lost */
  totalLosses: number;
  /** Total amount paid by losers */
  totalPaid: number;
}

/**
 * Interface for creating a new Paydle
 */
export interface CreatePaydleDto {
  /** The 5-letter word to guess */
  word: string;
  /** Amount to pay if player loses */
  price: number;
  /** Optional description/hint */
  description?: string;
  /** Creator ID */
  creatorId: string;
  /** Creator name */
  creatorName?: string;
}

/**
 * Interface for Paydle info returned to players (sanitized)
 */
export interface PaydlePlayInfo {
  /** Paydle ID */
  id: string;
  /** Amount to pay if lost */
  price: number;
  /** Optional description/hint */
  description?: string;
  /** Maximum attempts allowed */
  maxAttempts: number;
  /** Whether this Paydle is active */
  isActive: boolean;
  /** Creator's name */
  creatorName?: string;
  /** Total number of attempts */
  totalAttempts: number;
}

/**
 * Interface for guess submission response
 */
export interface GuessResponse {
  /** Status for each letter in the guess */
  letterStatuses: LetterStatus[];
  /** Current status of the attempt */
  status: PaydleStatus;
  /** Whether the player won */
  isWin: boolean;
  /** Whether the game is over */
  isGameOver: boolean;
  /** How many attempts are left */
  remainingAttempts: number;
  /** Message to display */
  message: string;
}

/**
 * Interface for keyboard letter state tracking
 */
export interface KeyboardLetterState {
  [letter: string]: LetterStatus;
}

/**
 * Interface for Paydle statistics
 */
export interface PaydleStats {
  /** The answer word */
  word: string;
  /** Price per loss */
  price: number;
  /** Total attempts */
  totalAttempts: number;
  /** Total wins */
  totalWins: number;
  /** Total losses */
  totalLosses: number;
  /** Win rate percentage */
  winRate: number;
}
